import React, { CSSProperties, useState } from 'react';
import { styled } from '@mui/material/styles';
import Menu from '@mui/material/Menu';
import Avatar from '@mui/material/Avatar';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { Link, MenuItem } from '@mui/material';
import Divider from '@mui/material/Divider';

export interface MenuAccountProps {
    id: string;
    userName: string;
    userProfileImageUrl?: string;
    menuItems: Array<MenuAccountItem>;
    iconSize?: number;
}

export interface MenuAccountItem {
    title: string;
    url: string;
    show: boolean;
    onClick?: () => void;
}

const MenuAccount = ({
    id,
    userName,
    userProfileImageUrl,
    menuItems,
    iconSize = 32
}: MenuAccountProps) => {
    const [ anchorEl, setAnchorEl ] = useState<HTMLElement | null>(null); 
    const divWidth = iconSize + 5;
    const handleOpenMenu = (event: React.MouseEvent<HTMLDivElement>) => {
        setAnchorEl(event.currentTarget);
    }

    const handleCloseMenu = () => {
        setAnchorEl(null);
    }

    const handleLinkClick = (externalFunction?: () => void) => 
        (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (externalFunction){
            event.preventDefault();
            externalFunction();
        }
    }

    return (
        <>
            <div 
                onClick={handleOpenMenu} 
                style={{ width: `${ divWidth }px`, cursor: 'pointer' }}
            >
                <CustomTooltip 
                    title={userName}
                    placement="bottom-end"
                >
                    <Avatar
                        alt="User Profile"
                        src={ userProfileImageUrl }
                        sx={{ width: iconSize, height: iconSize }}
                    />
                </CustomTooltip>
            </div>
            <Menu
                id={id}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                MenuListProps={{
                    'aria-labelledby': 'basic-button'
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                    sx: {
                        color: '#212529',
                        fontSize: '13px',
                        '& .MuiList-root': {
                            bgcolor: 'white',
                            paddingLeft: '0.5rem',
                            paddingRight: '0.5rem'
                        }
                    }
                }}
            >
                <MenuItem 
                    onClick={handleCloseMenu} 
                    sx={{ color: '#212529' }}
                >
                    { userName.toLowerCase() }
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                {
                    menuItems
                        .filter(item => item.show)
                        .map((item: MenuAccountItem, index) => (
                            <MenuItem
                                key={`${id}-item-${index}`} 
                                onClick={handleCloseMenu}
                                sx={{ paddingRight: '0.5rem', paddingLeft: '0.5rem' }}
                            >
                                <Link
                                    href={item.url}
                                    onClick={handleLinkClick(item.onClick)}
                                    underline="none"
                                    color="black"
                                    sx={{ 
                                        width: '100%',
                                        '&:hover': { color: '#FF9E00' } 
                                    }}
                                >
                                    { item.title }
                                </Link>
                            </MenuItem>
                        ))
                }
            </Menu>
        </>
    );
}

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: '#C4C4C4',
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#C4C4C4',
      color: theme.palette.common.black,
      fontSize: '13px'
    },
}));

export default MenuAccount;